import React, { useState, useEffect, useRef } from "react";
import "./IframeTestPage.css";

const IframeTestPage: React.FC = () => {
  const baseUrl = process.env.REACT_APP_ROUTER_BASE || "";

  const [messages, setMessages] = useState<
    Array<{ id: number; data: any; timestamp: string }>
  >([]);
  const [messageId, setMessageId] = useState(0);
  const [selectedPage, setSelectedPage] = useState("dashboard");

  // Available pages for navigation
  const availablePages = [
    // Market Overview
    {
      value: "dashboard",
      label: "Dashboard",
      url: `/${baseUrl}/dashboard?token=&theme=Direct`,
    },
    {
      value: "minichart",
      label: "Mini Chart",
      url: `/${baseUrl}/minichart?token=&region=AU&rics=CBA.ASX&showPeers=true&peerCount=10&theme=Direct`,
    },
    {
      value: "mktindices",
      label: "Market Indices",
      url: `/${baseUrl}/mktindices?token=&region=1`,
    },
    {
      value: "heatmap",
      label: "Market Heatmap",
      url: `/${baseUrl}/heatmap?token=&colorBy=pc&sizeBy=mktcap&showColorLegend=true`,
    },
    {
      value: "topmovers",
      label: "Top Movers",
      url: `/${baseUrl}/topmovers?token=&geography=ASX&rankType=AV`,
    },

    // News
    {
      value: "news",
      label: "News",
      url: `/${baseUrl}/news?token=&theme=Direct`,
    },
    {
      value: "fullnews",
      label: "Full News",
      url: `/${baseUrl}/fullnews?token=&theme=Direct&region=AU&sector=50;51;52;53;54;55;56;57;58;59;60&frDate=2025-09-29&toDate=2025-09-30`,
    },

    // Company Analysis
    {
      value: "ownership",
      label: "Ownership",
      url: `/${baseUrl}/ownership?token=&symbol=CBA.ASX`,
    },
    {
      value: "companyprofile",
      label: "Company Profile",
      url: `/${baseUrl}/companyprofile?token=&symbol=CBA.ASX`,
    },
    {
      value: "pricehistory",
      label: "Price History",
      url: `/${baseUrl}/pricehistory?token=&symbol=AAPL.NAS`,
    },
    {
      value: "brokerrating",
      label: "Broker Rating",
      url: `/${baseUrl}/brokerrating?token=&symbol=AAPL.NAS`,
    },
    {
      value: "targetprice",
      label: "Target Price",
      url: `/${baseUrl}/targetprice?token=&symbol=AAPL.NAS`,
    },
    {
      value: "companyscore",
      label: "Company Score",
      url: `/${baseUrl}/companyscore?token=&symbol=AAPL.NAS`,
    },
    {
      value: "incomestatement",
      label: "Income Statement",
      url: `/${baseUrl}/incomestatement?token=&symbol=AAPL.NAS&select=FY0`,
    },
    {
      value: "balancesheet",
      label: "Balance Sheet",
      url: `/${baseUrl}/balancesheet?token=&symbol=AAPL.NAS&select=FY0`,
    },
    {
      value: "cashflow",
      label: "Cash Flow",
      url: `/${baseUrl}/cashflow?token=&symbol=AAPL.NAS&select=FY0`,
    },

    // Indices & Sectors
    {
      value: "indices",
      label: "Indices",
      url: `/${baseUrl}/indices?token=&period=1M`,
    },
    {
      value: "sectorindices",
      label: "Sector Indices",
      url: `/${baseUrl}/sectorindices?token=&region=AU&period=3M&select=50`,
    },
    {
      value: "sectorheatmap",
      label: "Sector Heatmap",
      url: `/${baseUrl}/sectorheatmap?token=&region=AU&period=3M`,
    },

    // Comparisons
    {
      value: "stockpeercomparison",
      label: "Stock Peer Comparison",
      url: `/${baseUrl}/stockpeercomparison?token=&symbol=AAPL.NAS&region=AU&tab=summary&theme=Direct`,
    },
    {
      value: "indicespeercomparison",
      label: "Indices Peer Comparison",
      url: `/${baseUrl}/indicespeercomparison?token=&region=all&index=XAO&tab=summary&theme=Direct`,
    },
    {
      value: "sectorpeercomparison",
      label: "Sector Peer Comparison",
      url: `/${baseUrl}/sectorpeercomparison?token=&region=AU&sector=50&tab=summary&theme=Direct`,
    },

    // Recommendations
    {
      value: "consensus",
      label: "Consensus Recommendation",
      url: `/${baseUrl}/consensusrecommendation?token=&region=AU&type=0&theme=Direct`,
    },

    // Screener
    {
      value: "screener",
      label: "Screener",
      url: `/${baseUrl}/screener?token=&theme=Direct&region=AU&sector=50`,
    },
  ];

  const [url, setUrl] = useState(() => {
    const defaultPage = availablePages.find(
      (page) => page.value === selectedPage,
    );
    return defaultPage
      ? defaultPage.url
      : `/${baseUrl}/dashboard?token=&theme=Direct`;
  });
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Handle postMessage events from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const newMessage = {
        id: messageId,
        data: event.data,
        timestamp: new Date().toLocaleTimeString(),
      };

      setMessages((prev) => [newMessage, ...prev]);
      setMessageId((prev) => prev + 1);

      console.log("Received postMessage:", event.data);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [messageId]);

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
  };

  const handleLoadIframe = () => {
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  const clearMessages = () => {
    setMessages([]);
    setMessageId(0);
  };

  const handlePageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setSelectedPage(selectedValue);

    const selectedPageData = availablePages.find(
      (page) => page.value === selectedValue,
    );
    if (selectedPageData) {
      setUrl(selectedPageData.url);
    }
  };

  const highlightJson = (jsonString: string) => {
    console.log("highlightJson input:", jsonString);
    jsonString = jsonString
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return jsonString.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      function (match) {
        var cls = "number";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key";
          } else {
            cls = "string";
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean";
        } else if (/null/.test(match)) {
          cls = "null";
        }
        return '<span class="' + cls + '">' + match + "</span>";
      },
    );
  };

  return (
    <div className="iframe-test-container">
      <div className="iframe-test-controls">
        <div className="url-input-section">
          <label htmlFor="url-input">URL:</label>
          <div className="url-input-container">
            <input
              id="url-input"
              type="url"
              value={url}
              onChange={handleUrlChange}
              placeholder="Enter URL for iframe"
              className="url-input"
            />
            <button onClick={handleLoadIframe} className="load-button">
              Load Iframe
            </button>
          </div>

          <div className="page-selector-section">
            <label htmlFor="page-select" className="page-select-label">
              Quick Select Page:
            </label>
            <select
              id="page-select"
              value={selectedPage}
              onChange={handlePageChange}
              className="page-select"
            >
              {availablePages.map((page) => (
                <option key={page.value} value={page.value}>
                  {page.label}
                </option>
              ))}
            </select>
            <button onClick={clearMessages} className="clear-messages-button">
              Clear Messages
            </button>
          </div>
        </div>
      </div>

      <div className="iframe-test-content">
        <div className="iframe-section">
          <h3>Iframe Content</h3>
          <iframe
            ref={iframeRef}
            src={url}
            className="test-iframe"
            title="Test Iframe"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>

        <div className="messages-section">
          <h3>PostMessage Events ({messages.length})</h3>
          <div className="messages-container">
            {messages.length === 0 ? (
              <p className="no-messages">No messages received yet</p>
            ) : (
              messages.map((message) => (
                <div key={message.id} className="message-item">
                  <div className="message-header">
                    <span className="message-timestamp">
                      {message.timestamp}
                    </span>
                    <span className="message-id">ID: {message.id}</span>
                  </div>
                  <div className="message-data">
                    {typeof message.data === "string" ? (
                      <pre className="plain-text">{message.data}</pre>
                    ) : (
                      <pre
                        className="json-text"
                        dangerouslySetInnerHTML={{
                          __html: highlightJson(
                            JSON.stringify(message.data, null, 2),
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IframeTestPage;
